import ExploreEuropeCountries from '@/components/explore/ExploreEuropeCountries';
import ExploreNearest from '@/components/explore/ExploreNearest';
import GolfClubSearch from '@/components/GolfClubSearch';
import HorizontalScrollMedia from '@/components/HorizontalScrollMedia';
import { ImageWithFallback } from '@/components/ui/fallbackImage';
import { api } from '@/utils/api';
import { getGeolocationWithCache } from '@/utils/geolocation';
import {
  IonButton,
  IonContent,
  IonHeader,
  IonPage,
  IonRouterLink,
  IonSpinner,
  IonText,
  IonToolbar
} from '@ionic/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import type { GolfClub } from './GolfSearch';

const FEATURED_COUNTRIES_WITH_STATES = ['France'];
const FEATURED_COUNTRIES = ['Italy', 'Spain', 'Germany', 'Switzerland', 'Scotland', 'Ireland', 'England', 'Sweden', 'Netherlands', 'Finland'];

const ClubsExplorer: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  // State variables
  const [nearbyClubs, setNearbyClubs] = useState<GolfClub[]>([]);
  const [userLocation, setUserLocation] = useState<{ latitude: number, longitude: number, city?: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [featuredStates, setFeaturedStates] = useState<Record<string, any[]>>({});

  // Fetch featured clubs by country
  const fetchFeaturedClubsByCountry = async () => {
    try {
      const statesByCountry: Record<string, any[]> = {};

      // Fetch and process clubs for each featured country
      for (const country of FEATURED_COUNTRIES_WITH_STATES) {
        const { data } = await api.get('/golf-clubs/by-country', {
          params: { country }
        });

        statesByCountry[country] = data;
      }
      return statesByCountry;
    } catch (error) {
      console.error('Error fetching featured clubs by country:', error);
      return {};
    }
  };

  // Fetch nearby golf clubs based on user location
  const fetchNearbyClubs = async (latitude: number, longitude: number) => {
    try {
      const { data } = await api.get('/golf-clubs', {
        params: {
          latitude,
          longitude,
          limit: 10,
          sortBy: 'distance'
        }
      });

      return data || [];
    } catch (error) {
      console.error('Error fetching nearby golf clubs:', error);
      return [];
    }
  };

  // Initialize data
  useEffect(() => {
    const initData = async () => {
      setIsLoading(true);

      // Get user location
      try {
        const geodata = await getGeolocationWithCache();
        // Cast the geodata to include the city property
        const locationData = geodata as { latitude: number; longitude: number; city?: string };
        setUserLocation({
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          city: locationData.city
        });

        // Fetch nearby clubs using location
        const nearby = await fetchNearbyClubs(geodata.latitude, geodata.longitude);
        setNearbyClubs(nearby);
      } catch (error) {
        console.error('Error getting location:', error);
      }

      // Fetch featured clubs by country
      const featuredByCountry = await fetchFeaturedClubsByCountry();
      setFeaturedStates(featuredByCountry);

      setIsLoading(false);
    };

    initData();
  }, []);

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar className='px-2 py-0'>
          <GolfClubSearch
            mode="Search"
            className='ring-2 ring-input/50 shadow-md hover:bg-input/50 rounded-full max-w-xl'
            customSearchCaption={t('search_golf_course_placeholder')}
            onCourseSelect={(course) => {
              history.push(`/c/${course.slug}`);
            }}
          />
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Golf Near Me Section */}
        <ExploreNearest
          nearbyClubs={nearbyClubs}
          isLoading={isLoading}
          userLocation={userLocation}
        />

        {/* Featured Countries with States */}
        {FEATURED_COUNTRIES_WITH_STATES.map(countryName => (
          <div key={countryName}>
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-semibold ml-4">{t('Headlines:explore_' + countryName.toLowerCase())}</h2>
              <IonButton fill="clear" size="small" routerLink={`/s?country=${countryName.toLowerCase()}`}>
                {t('see_all')}
              </IonButton>
            </div>
            <HorizontalScrollMedia>
              {isLoading ? (
                <IonSpinner />
              ) : featuredStates[countryName]?.length > 0 ? (
                featuredStates[countryName].map((state) => (
                  <IonRouterLink
                    className="cursor-pointer ml-4 overflow-hidden"
                    key={state.slug}
                    routerLink={`/s?country=${countryName.toLowerCase()}&state=${state.slug}`}
                  >
                    <div className="relative group">
                      <ImageWithFallback
                        alt={`${state.name} photo`}
                        fallbackImage="/assets/images/fallback.jpg"
                        src={`/assets/images/location/${countryName.toLowerCase()}/${state.slug}.webp`}
                        className="w-48 h-52 md:w-56 md:h-64 object-cover rounded-md brightness-90"
                      />
                      <div className="absolute inset-0 shadow-lg flex items-end justify-start p-4">
                        <span className="text-white text-lg font-bold break-words" style={{ textShadow: '1px 1px 0 rgba(0,0,0,0.35), 1px 1px 5px rgba(0,0,0,0.5)' }}>{state.name}</span>
                      </div>
                    </div>
                  </IonRouterLink>
                ))
              ) : (
                <div className="flex justify-center items-center w-full p-4">
                  <IonText color="medium">{t("no_clubs_found")}</IonText>
                </div>
              )}
            </HorizontalScrollMedia>
          </div>
        ))}

        <ExploreEuropeCountries countries={FEATURED_COUNTRIES} isLoading={isLoading} />
      </IonContent>
    </IonPage>
  );
};

export default ClubsExplorer;